/**
 * Round 26 — Birth-time rectification modal.
 *
 * Three-step flow:
 *   1. Confirm window (±15/30/45/60m around current birth time)
 *   2. Optional life events (up to 3, type + date)
 *   3. Confirm ₹2,000 wallet charge
 *
 * Result screens: HIGH / MEDIUM / LOW (with auto-refund on LOW).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, readErr } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useRecharge } from '@/components/RechargeProvider';
import { fetchRegion, formatPrice } from '@/lib/region';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/lib/toast';
import ProfilePicker from '@/components/ProfilePicker';

// Round 47 — Price fetched from /api/pricing/btr (DB-backed,
// admin-editable). Hardcoded values are bootstrap fallbacks only.
const FALLBACK_LIST_BTR  = { INR: 2000, USD: 70 };
const FALLBACK_FINAL_BTR = { INR: 2000, USD: 42 };

const EVENT_TYPES = [
    { v: 'marriage',         label: 'Marriage' },
    { v: 'first_job',        label: 'First job' },
    { v: 'bought_property',  label: 'Bought property' },
    { v: 'child',            label: 'Child birth' },
    { v: 'other',            label: 'Other' },
];

const WINDOW_OPTIONS = [15, 30, 45, 60];

export default function RectifyBirthTimeModal({ open, onOpenChange, onSuccess, targetProfile = null }) {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const { open: openRecharge } = useRecharge();

    // Round 43b — Pull wallet from /wallet/balance (single source of truth
    // used by AppShell). user.wallet_balance_inr was stale for USD users
    // and showed $0 in the insufficient-funds gate even when the actual
    // wallet was $1,489. Refresh on modal open.
    const [walletInr, setWalletInr] = useState(Number(user?.wallet_balance_inr ?? 0));
    useEffect(() => {
        if (!open) return undefined;
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get('/wallet/balance');
                if (alive) setWalletInr(Number(data?.balance_inr ?? 0));
            } catch { /* fall back to user object */ }
        })();
        return () => { alive = false; };
    }, [open]);

    // Round 47 — region-aware BTR price + admin-editable discount.
    const [currency, setCurrency] = useState('INR');
    const [priceListAmt, setPriceListAmt]   = useState(null);
    const [priceFinalAmt, setPriceFinalAmt] = useState(null);
    const [discountPct, setDiscountPct]     = useState(0);
    useEffect(() => {
        let alive = true;
        fetchRegion()
            .then((r) => { if (alive) setCurrency(r?.currency || 'INR'); })
            .catch(() => { /* default INR — modal still works */ });
        api.get('/pricing/btr')
            .then(({ data }) => {
                if (!alive) return;
                setCurrency(data.currency || 'INR');
                setPriceListAmt(Number(data.list_amount));
                setPriceFinalAmt(Number(data.final_amount));
                setDiscountPct(Number(data.discount_pct || 0));
            })
            .catch(() => { /* fallback values in place */ });
        return () => { alive = false; };
    }, []);
    const listAmount  = priceListAmt  ?? FALLBACK_LIST_BTR[currency]  ?? FALLBACK_LIST_BTR.INR;
    const finalAmount = priceFinalAmt ?? FALLBACK_FINAL_BTR[currency] ?? FALLBACK_FINAL_BTR.INR;
    const costAmount  = finalAmount;
    const costLabel   = formatPrice(costAmount, currency);
    const listLabel   = formatPrice(listAmount, currency);
    const showDiscount = discountPct > 0 && listAmount > finalAmount;
    const hasFunds = walletInr >= costAmount;

    // Round 33 — profile-aware. Defaults to the user's default profile;
    // user can pick another (spouse, child, etc.) before submitting.
    // Round 33b — when invoked from a specific profile row, the picker is
    // hidden and the target profile is locked in via the `targetProfile` prop.
    const [profileId, setProfileId] = useState(targetProfile?.id || null);
    const [activeProfile, setActiveProfile] = useState(targetProfile || null);
    const lockedToProfile = !!targetProfile;
    const currentTime = activeProfile?.birth_details?.time
        || user?.birth_details?.time
        || '08:00';

    const [step, setStep] = useState(1);
    const [windowMin, setWindowMin] = useState(30);
    const [events, setEvents] = useState([{ type: '', date: '' }]);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    // Sync state when the modal opens with a fresh target profile (e.g.
    // user clicks Rectify on a different row without remounting).
    useEffect(() => {
        if (open && targetProfile) {
            setProfileId(targetProfile.id);
            setActiveProfile(targetProfile);
        }
    }, [open, targetProfile]);

    const reset = () => {
        setStep(1);
        setWindowMin(30);
        setEvents([{ type: '', date: '' }]);
        setResult(null);
        if (!lockedToProfile) {
            setProfileId(null);
            setActiveProfile(null);
        }
    };

    const close = () => {
        onOpenChange(false);
        // Defer reset so the closing animation doesn't show a flash of step 1.
        setTimeout(reset, 200);
    };

    const addEvent = () => {
        if (events.length >= 3) return;
        setEvents([...events, { type: '', date: '' }]);
    };

    const updateEvent = (idx, patch) => {
        const next = events.slice();
        next[idx] = { ...next[idx], ...patch };
        setEvents(next);
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            const cleanEvents = events
                .filter((e) => e.type && e.date)
                .map((e) => ({ type: e.type, date: e.date }));
            const { data } = await api.post('/astrology/rectify-birth-time', {
                approx_birth_time: currentTime,
                window_minutes: windowMin,
                life_events: cleanEvents,
                profile_id: profileId,
            });
            setResult(data);
            await refresh();
            if (onSuccess) onSuccess(data);
        } catch (err) {
            toast.error(readErr(err, 'Analysis failed — please try again later.'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => v ? onOpenChange(v) : close()}>
            <DialogContent
                className="bg-elevated border-marigold/20 text-parchment max-w-lg p-0 overflow-hidden"
                data-testid="rectify-modal"
            >
                {result ? (
                    <ResultScreen
                        result={result}
                        costLabel={costLabel}
                        onClose={close}
                        onBookConsultation={() => { close(); navigate('/app/consult'); }}
                        onConfirmed={async () => { await refresh(); close(); }}
                    />
                ) : (
                    <div className="p-8">
                        <DialogHeader>
                            <div className="overline mb-2">Birth-time analysis · Step {step} of 3</div>
                            <DialogTitle className="font-serif text-2xl text-parchment">
                                {step === 1 && 'Confirm your window'}
                                {step === 2 && 'Life events (optional)'}
                                {step === 3 && 'Confirm analysis'}
                            </DialogTitle>
                        </DialogHeader>

                        {step === 1 && (
                            <div className="mt-6 space-y-4" data-testid="rectify-step-1">
                                {lockedToProfile ? (
                                    <div data-testid="rectify-locked-profile" className="text-sm text-slate-300 bg-surface border border-marigold/15 rounded-sm px-4 py-3">
                                        Analysing birth time for{' '}
                                        <span className="text-parchment font-medium">{activeProfile?.name}</span>
                                        {activeProfile?.relation && activeProfile.relation !== 'self' && (
                                            <span className="text-slate-500"> · {activeProfile.relation}</span>
                                        )}
                                    </div>
                                ) : (
                                    <div data-testid="rectify-profile-section">
                                        <Label className="text-xs text-slate-400 uppercase tracking-wider">Whose birth time are we analysing?</Label>
                                        <div className="mt-2">
                                            <ProfilePicker
                                                value={profileId}
                                                onChange={(id, p) => { setProfileId(id); setActiveProfile(p); }}
                                                testIdPrefix="rectify-profile"
                                                compact
                                            />
                                        </div>
                                    </div>
                                )}
                                <p className="text-sm text-slate-400">
                                    Birth time on file is{' '}
                                    <span className="text-parchment font-medium">{currentTime}</span>.
                                    How much variation is possible around this time?
                                </p>
                                <div className="grid grid-cols-2 gap-2">
                                    {WINDOW_OPTIONS.map((w) => (
                                        <button
                                            key={w}
                                            type="button"
                                            onClick={() => setWindowMin(w)}
                                            data-testid={`rectify-window-${w}`}
                                            className={`p-3 rounded-sm border text-sm transition-all ${
                                                windowMin === w
                                                    ? 'bg-marigold/10 border-marigold text-parchment'
                                                    : 'bg-surface border-marigold/10 text-slate-300 hover:border-marigold/30'
                                            }`}
                                        >
                                            ± {w} minutes
                                        </button>
                                    ))}
                                </div>
                                <div className="flex justify-end pt-2">
                                    <Button
                                        onClick={() => setStep(2)}
                                        data-testid="rectify-next-1"
                                        className="bg-marigold text-obsidian hover:bg-marigold-hover rounded-none"
                                    >
                                        Next →
                                    </Button>
                                </div>
                            </div>
                        )}

                        {step === 2 && (
                            <div className="mt-6 space-y-4" data-testid="rectify-step-2">
                                <p className="text-sm text-slate-400">
                                    Adding a known life event can improve accuracy. This step is optional.
                                </p>
                                {events.map((ev, i) => (
                                    <div key={i} className="grid grid-cols-2 gap-3">
                                        <select
                                            value={ev.type}
                                            onChange={(e) => updateEvent(i, { type: e.target.value })}
                                            className="bg-surface border border-marigold/15 rounded-sm text-parchment h-10 px-3 text-sm"
                                            data-testid={`rectify-event-type-${i}`}
                                        >
                                            <option value="">Choose event…</option>
                                            {EVENT_TYPES.map((t) => (
                                                <option key={t.v} value={t.v}>{t.label}</option>
                                            ))}
                                        </select>
                                        <Input
                                            type="date"
                                            value={ev.date}
                                            onChange={(e) => updateEvent(i, { date: e.target.value })}
                                            className="bg-surface border-marigold/15 rounded-sm text-parchment h-10"
                                            data-testid={`rectify-event-date-${i}`}
                                        />
                                    </div>
                                ))}
                                {events.length < 3 && (
                                    <button
                                        type="button"
                                        onClick={addEvent}
                                        className="text-xs text-marigold/80 hover:text-marigold"
                                        data-testid="rectify-event-add"
                                    >
                                        + Add another event
                                    </button>
                                )}
                                <p className="text-xs text-slate-500">
                                    These events are used only for this analysis.
                                </p>
                                <div className="flex justify-between pt-2">
                                    <button
                                        type="button"
                                        onClick={() => setStep(3)}
                                        className="text-sm text-slate-400 hover:text-parchment"
                                        data-testid="rectify-skip"
                                    >
                                        Skip →
                                    </button>
                                    <Button
                                        onClick={() => setStep(3)}
                                        data-testid="rectify-next-2"
                                        className="bg-marigold text-obsidian hover:bg-marigold-hover rounded-none"
                                    >
                                        Next →
                                    </Button>
                                </div>
                            </div>
                        )}

                        {step === 3 && (
                            <div className="mt-6 space-y-4" data-testid="rectify-step-3">
                                <div className="bg-surface border border-marigold/15 rounded-sm p-5 space-y-3">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-400">Service</span>
                                        <span className="text-parchment">Birth-time analysis</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-400">Cost</span>
                                        <span className="font-medium text-parchment flex items-center gap-2" data-testid="rectify-cost-line">
                                            {showDiscount && (
                                                <>
                                                    <span className="line-through text-slate-500 text-xs" data-testid="rectify-list-price">{listLabel}</span>
                                                    <span
                                                        className="px-1.5 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-[10px] font-bold uppercase tracking-wider"
                                                        data-testid="rectify-discount-badge"
                                                    >
                                                        {discountPct}% OFF
                                                    </span>
                                                </>
                                            )}
                                            <span data-testid="rectify-final-price">{costLabel}</span>
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-sm border-t border-marigold/10 pt-3">
                                        <span className="text-slate-400">Your wallet</span>
                                        <span className={`font-medium ${
                                            hasFunds ? 'text-parchment' : 'text-red-400'
                                        }`} data-testid="rectify-balance">
                                            {formatPrice(walletInr, currency)}
                                        </span>
                                    </div>
                                </div>
                                {!hasFunds && (
                                    <div className="text-sm text-red-400" data-testid="rectify-insufficient">
                                        You need {costLabel} in your wallet for this analysis.{' '}
                                        <button
                                            type="button"
                                            onClick={() => openRecharge({
                                                onSuccess: () => {
                                                    // Refresh the auth store so walletInr
                                                    // updates immediately — modal stays open
                                                    // so the user can proceed straight to submit.
                                                    if (typeof refresh === 'function') refresh();
                                                },
                                            })}
                                            className="text-marigold hover:text-marigold-50 underline"
                                        >
                                            Top up
                                        </button>
                                    </div>
                                )}
                                <p className="text-xs text-slate-500">
                                    If the analysis confidence is low, your {costLabel} is returned in full.
                                </p>
                                <div className="flex justify-between pt-2">
                                    <button
                                        type="button"
                                        onClick={close}
                                        className="text-sm text-slate-400 hover:text-parchment"
                                        data-testid="rectify-cancel"
                                    >
                                        Not now
                                    </button>
                                    <Button
                                        onClick={submit}
                                        disabled={submitting || !hasFunds}
                                        data-testid="rectify-submit"
                                        className="bg-marigold text-obsidian hover:bg-marigold-hover rounded-none"
                                    >
                                        {submitting ? 'Analysing…' : 'Analyse Birth Time'}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}


/**
 * Round 29 A52 — Confirmation-first result screen.
 *
 * Birth time rectification suggests a candidate; it never auto-confirms.
 * The user must explicitly choose to update their profile. The KP Ruling
 * Planets method is sensitive to the moment of analysis and can suggest
 * a time further from the truth than what the user originally entered.
 * Three states:
 *   - Inconclusive (low/none confidence) → no action, wallet refunded.
 *   - Large deviation (>30 min)         → review carefully, "Keep my time" prominent.
 *   - Small deviation (≤30 min)         → standard confirm flow.
 */
function ResultScreen({ result, costLabel, onClose, onBookConsultation, onConfirmed }) {
    const [submitting, setSubmitting] = useState(false);
    const c = result.confidence;
    const time = result.rectified_time_local;
    const enteredTime = result.entered_time_local;
    const lagna = result.lagna_sign;
    const deviation = result.deviation_minutes;
    const warning = result.large_deviation_warning;
    const gatesPassed = result.csl_gate_passed || [];
    const gatesFailed = result.csl_gate_failed || [];
    const chain = [result.sign_lord, result.star_lord, result.sub_lord, result.sub_sub_lord]
        .filter(Boolean).join(' · ');

    const decide = async (confirmed) => {
        if (!result.rectification_id) {
            onClose();
            return;
        }
        setSubmitting(true);
        try {
            await api.post('/astrology/rectify-birth-time/confirm', {
                rectification_id: result.rectification_id,
                confirmed,
            });
            if (confirmed) {
                toast.success(`Birth time updated to ${time}.`);
            } else {
                toast.message(`Kept your current birth time (${enteredTime}).`);
            }
            if (onConfirmed) await onConfirmed();
            else onClose();
        } catch (err) {
            toast.error(readErr(err, 'Could not save your choice — please try again.'));
        } finally {
            setSubmitting(false);
        }
    };

    // STATE 3 — Inconclusive: low/none confidence, wallet already refunded.
    if (c === 'low' || c === 'none' || !result.success) {
        return (
            <ResultLayout title="Inconclusive result" testId="rectify-result-low">
                <p className="text-sm text-slate-300">
                    The analysis found multiple possible birth times within your window.
                </p>
                <p className="text-sm text-slate-400">
                    A definitive result requires a human astrologer who can cross-reference
                    your life events in depth.
                </p>
                <p className="text-sm text-marigold/90">
                    Your {costLabel} has been returned to your wallet.
                </p>
                <div className="flex justify-between pt-2">
                    <Button
                        onClick={onClose}
                        data-testid="rectify-keep-current-low"
                        className="bg-marigold text-obsidian hover:bg-marigold-hover rounded-none"
                    >
                        Keep my current time
                    </Button>
                    <button
                        onClick={onBookConsultation}
                        data-testid="rectify-book-low"
                        className="text-sm text-marigold hover:text-marigold-50"
                    >
                        Book consultation →
                    </button>
                </div>
            </ResultLayout>
        );
    }

    const gateLine = gatesPassed.length ? (
        <p className="text-xs text-emerald-400/90" data-testid="rectify-gate-pass">
            The chart at this time also confirms your{' '}
            <span className="text-parchment">{gatesPassed.join(', ')}</span>{' '}
            — the relevant house sub-lord connects to the correct houses.
        </p>
    ) : gatesFailed.length ? (
        <p className="text-xs text-amber-400/90" data-testid="rectify-gate-fail">
            Note: the chart at this time does not clearly promise{' '}
            <span className="text-parchment">{gatesFailed.join(', ')}</span>{' '}
            — treat this suggestion with extra caution.
        </p>
    ) : null;

    const ChainBlock = (
        <div className="bg-surface border border-marigold/10 rounded-sm p-4 space-y-1">
            <div className="text-xs uppercase tracking-widest text-copper">Ruling chain at this time</div>
            <div className="text-sm text-parchment">
                Ascendant <span className="text-marigold">{lagna}</span> — {chain}
            </div>
        </div>
    );

    // STATE 2 — Large deviation: keep-current is the visually dominant choice.
    if (warning && deviation > 30) {
        return (
            <ResultLayout title="Review carefully before confirming" testId="rectify-result-large-dev">
                <p className="text-sm text-slate-300">
                    The analysis suggests <span className="text-marigold font-medium">{time}</span> —
                    but this is <span className="text-parchment">{deviation} minutes</span> from your
                    entered time of <span className="text-parchment">{enteredTime}</span>.
                </p>
                <p className="text-sm text-amber-400/90">
                    The KP method is sensitive to the moment of analysis. If this does not match
                    what you know about your birth, keep your current time.
                </p>
                {ChainBlock}
                {gateLine}
                <div className="flex flex-col gap-2 pt-2">
                    <Button
                        onClick={() => decide(false)}
                        disabled={submitting}
                        data-testid="rectify-keep-current"
                        className="w-full bg-marigold text-obsidian hover:bg-marigold-hover rounded-none h-11"
                    >
                        Keep my current time ({enteredTime})
                    </Button>
                    <button
                        onClick={() => decide(true)}
                        disabled={submitting}
                        data-testid="rectify-update-anyway"
                        className="text-xs text-slate-500 hover:text-slate-300 underline self-center"
                    >
                        Update to {time} anyway
                    </button>
                </div>
            </ResultLayout>
        );
    }

    // STATE 1 — Small deviation, high/medium confidence: standard confirm flow.
    return (
        <ResultLayout title="Analysis complete" testId="rectify-result-confirm">
            <p className="text-sm text-slate-300">
                The analysis points to <span className="text-marigold font-medium">{time}</span>{' '}
                as your birth time.
                {Number.isFinite(deviation) && (
                    <>
                        {' '}This is <span className="text-parchment">
                            {deviation === 0 ? 'the same as' : `${deviation} minutes from`}
                        </span>{' '}
                        your entered time of <span className="text-parchment">{enteredTime}</span>.
                    </>
                )}
            </p>
            {ChainBlock}
            {gateLine}
            <div className="flex flex-col gap-2 pt-2">
                <Button
                    onClick={() => decide(true)}
                    disabled={submitting}
                    data-testid="rectify-confirm"
                    className="w-full bg-marigold text-obsidian hover:bg-marigold-hover rounded-none h-11"
                >
                    {submitting ? 'Saving…' : `Update my birth time to ${time}`}
                </Button>
                <Button
                    onClick={() => decide(false)}
                    disabled={submitting}
                    variant="outline"
                    data-testid="rectify-decline"
                    className="w-full bg-transparent border border-marigold/40 text-parchment hover:bg-marigold/10 rounded-none h-11"
                >
                    Keep my current time ({enteredTime})
                </Button>
            </div>
        </ResultLayout>
    );
}


function ResultLayout({ title, testId, children }) {
    return (
        <div className="p-8 space-y-4" data-testid={testId}>
            <DialogHeader>
                <div className="overline mb-2">Birth-time analysis</div>
                <DialogTitle className="font-serif text-2xl text-parchment">
                    {title}
                </DialogTitle>
            </DialogHeader>
            {children}
        </div>
    );
}
